import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";
import dayjs from "dayjs";
import "dayjs/locale/ko"; // 한국어 로케일 불러오기
import MonthSelector from "../../components/calendar/MonthSelector";
import { ReviewInfo, ReviewResponseData } from "../../api/soollogApi";
import soollogApi from "../../api/soollogApi";
import ReviewList from "../../components/review/ReviewList";
import logger from "@/utils/logger";

// Day.js 한국어 로케일 설정
dayjs.locale("ko");

const SoolLogPage: React.FC = () => {
    // 현재 표시할 날짜 상태
    const [currentDate, setCurrentDate] = useState(dayjs());

    // 리뷰 목록 상태
    const [reviews, setReviews] = useState<ReviewInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0); // 페이지 번호를 0부터 시작
    const [hasMore, setHasMore] = useState(false);

    // 날짜 변경 핸들러
    const handleDateChange = (newDate: dayjs.Dayjs) => {
        setCurrentDate(newDate);
        // 날짜가 변경되면 리뷰 목록 초기화하고 다시 불러오기
        setReviews([]);
        setPage(0);
        fetchReviews(0, newDate);
    };

    // 리뷰 목록 가져오기
    const fetchReviews = useCallback(
        async (pageNum: number, date = currentDate) => {
            try {
                setLoading(true);
                const year = date.year();
                const month = date.month() + 1; // dayjs는 월이 0부터 시작하므로 +1

                const response = await soollogApi.fetchSoolLogReviews(
                    pageNum,
                    5, // 한 페이지에 5개씩 표시
                    year,
                    month
                );

                if (pageNum === 0) {
                    setReviews(response.reviewInfoList);
                } else {
                    setReviews((prev) => [...prev, ...response.reviewInfoList]);
                }

                setHasMore(response.pagination.nextPage !== null);
                setPage(pageNum);
                setError(null);
            } catch (err) {
                logger.error("리뷰를 가져오는 중 오류 발생:", err);
                setError("리뷰를 불러올 수 없습니다. 다시 시도해주세요.");
            } finally {
                setLoading(false);
            }
        },
        [currentDate]
    );

    // 첫 로딩 시 리뷰 목록 가져오기
    useEffect(() => {
        fetchReviews(0);
    }, [fetchReviews]);

    // 더 불러오기 핸들러
    const handleLoadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            fetchReviews(nextPage);
        }
    };

    return (
        <Container>
            <HeaderSection>
                <Title>술로그</Title>
                <DateSelectorContainer>
                    <MonthSelector currentDate={currentDate} onDateChange={handleDateChange} />
                </DateSelectorContainer>
            </HeaderSection>

            <ContentSection>
                <ReviewListWrapper>
                    <ReviewList
                        reviews={reviews}
                        isLoading={loading}
                        error={error}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMore}
                    />
                </ReviewListWrapper>
            </ContentSection>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 430px;
    min-width: 320px;
    height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    background: ${palette.white};
    overflow-y: auto;
`;

const HeaderSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${palette.gray[200]};
    background-color: ${palette.white};
    padding-top: 12px;
`;

const Title = styled.h1`
    ${typography.title2}
    color: ${palette.gray[900]};
    text-align: center;
    margin-bottom: 16px;
`;

const DateSelectorContainer = styled.div`
    padding: 0 16px 12px;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    padding-bottom: 80px; /* 바텀 네비게이션 높이보다 크게 패딩 추가 */
`;

const ReviewListWrapper = styled.div`
    padding: 20px 16px;
`;

export default SoolLogPage;
